/**
 * Crash recovery for the one unsaved new plan: the writer that mirrors it into `localStorage`, the
 * reader that offers it back, and the acts that end it.
 *
 * `model/unsavedPlanRecord.ts` decides what the stored bytes mean and `model/leftoverPlan.ts` what a
 * prompt offers; this file is the I/O around them.
 *
 * ── What is persisted, and what deliberately is not ──────────────────────────────────────────
 * ONLY a plan with `id === null` — a seed from the create modal that exists nowhere but this tab.
 * A stored plan is never written, and loading one never clears the record: a trainer who leaves
 * `/plans/nowy` to look at another plan and comes back through „+ Nowy program" still has the new
 * plan waiting. The document-model ADR §6 declines persisting EDITS to a stored plan, because a
 * replayed edit lands on a server tree that may have moved on; its amendment of 23 September 2026
 * records this exception. The record is the whole document, restored whole and only on request.
 *
 * ── The tab's unsaved plan carries three facts the tree does not ─────────────────────────────
 * `tab` sits next to the store's unsaved draft and is dropped the moment the store stops holding
 * one. It names:
 *
 * · **the OWNER** — the user who started it. A write for anybody else is refused, so a plan left in
 *   memory across a session change can never be stamped as the next trainer's. The session end
 *   drops the draft itself (`dropUnsavedPlanFromTab`, wired in `app/main.tsx`); this is the second
 *   wall behind it.
 * · **the DOCUMENT** — a key minted once per seed. Two tabs building two plans share one device key;
 *   a tab that saves or starts afresh removes the record only when it is still ITS plan's.
 * · **whether the device HOLDS it** — the last write for this document succeeded. The leave guard
 *   promises a week only when that is true (`useDeviceCopyHeld`).
 *
 * ── A first save in flight writes one marker and nothing else ────────────────────────────────
 * From the POST to its answer the server may already hold the plan. A copy written in that window
 * — a pagehide flush as the tab closes — would come back as "unsaved" and be POSTed a second time.
 * So the save's start writes the copy once with `saveStartedAt`, every later write waits for the
 * answer, and a copy that survives with the marker is offered back with a warning.
 *
 * ── One record per device, and every touch is best-effort ────────────────────────────────────
 * `localStorage` can refuse on every access — the property itself throws in Safari's private mode
 * and with site data blocked, and `setItem` throws on a full quota — so every access is fenced and a
 * failure means "no record". Losing the recovery copy is a degradation; taking the builder down
 * with it would be the defect.
 *
 * ── When a write lands ───────────────────────────────────────────────────────────────────────
 * At once when a plan is seeded, then debounced on every change to the tree (`WRITE_DEBOUNCE_MS`),
 * because the whole tree is serialised each time and typing a weight is one change per keystroke.
 * `pagehide` and a tab going hidden FLUSH the debounce synchronously, so a reload a moment after an
 * edit keeps that edit.
 */

import { useSyncExternalStore } from 'react';

import { preferenceStore } from '~/shared/lib/preferenceStore';

import { newPlanEntry, type LeftoverPlan, type NewPlanEntry } from '../model/leftoverPlan';
import type { PlanDraft } from '../model/planDraft.types';
import { newSlotToken } from '../model/slotToken';
import {
  judgeUnsavedPlanRecord,
  mayWriteUnsavedPlanRecord,
  recordHoldsDocument,
  serialiseUnsavedPlanRecord,
  type UnsavedPlanRecord,
} from '../model/unsavedPlanRecord';

import {
  discardUnsavedPlanDraft,
  loadPlanDraft,
  readPlanDraft,
  readPlanRecipient,
  seedPlanDraft,
  subscribeToPlanDraftChanges,
  type PlanRecipient,
} from './planDraftStore';

/** The one key. Versioned INSIDE the record (`schema`), never in the name. */
const RECORD_KEY = 'movebuddy.planBuilder.unsavedPlan';

/**
 * How long the writer waits for the trainer to stop typing. Long enough that a typed number is one
 * write rather than four; short enough that a crash between two edits loses a fraction of a second
 * of work. The hide flush covers the case the debounce cannot: a reload fired inside the window.
 */
const WRITE_DEBOUNCE_MS = 600;

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Module state. One subscription for the page's lifetime — started once from `app/main.tsx`,
// never by a component.
// ─────────────────────────────────────────────────────────────────────────────────────────────

interface TabDocument {
  readonly documentKey: string;
  readonly ownerId: string | null;
  held: boolean;
  /** A first save is on the wire: every write but its marker waits for the answer. */
  saveInFlight: boolean;
  /** Carried in every write until the server answers either way. See the header. */
  saveStartedAt: number | null;
  /**
   * The plan stopped being unsaved work without leaving the store: it was assigned to its client and
   * stays on screen under the confirmation, still with no id. Nothing about it is written again.
   */
  settled: boolean;
}

let tab: TabDocument | null = null;
let pendingWrite: ReturnType<typeof setTimeout> | null = null;
let signedInUser: () => string | null = () => null;
const heldListeners = new Set<() => void>();

function notifyHeld(): void {
  for (const listener of heldListeners) listener();
}

function setHeld(held: boolean): void {
  if (tab === null || tab.held === held) return;
  tab.held = held;
  notifyHeld();
}

/** Describe a new unsaved document, or none. Every replacement is announced to `useDeviceCopyHeld`. */
function replaceTab(next: TabDocument | null): void {
  tab = next;
  notifyHeld();
}

function forgetTab(): void {
  if (tab !== null) replaceTab(null);
}

function cancelPendingWrite(): void {
  if (pendingWrite === null) return;
  clearTimeout(pendingWrite);
  pendingWrite = null;
}

function readRaw(): string | null {
  try {
    return preferenceStore()?.getItem(RECORD_KEY) ?? null;
  } catch {
    return null;
  }
}

function removeRecord(): void {
  try {
    preferenceStore()?.removeItem(RECORD_KEY);
  } catch {
    // Best-effort: a removal that cannot happen leaves a record the reader judges again next time.
  }
}

/** The record as this user may read it — pure: nothing is removed here. */
function readRecord(userId: string): UnsavedPlanRecord | null {
  const judged = judgeUnsavedPlanRecord(readRaw(), userId, Date.now());
  return judged.verdict === 'usable' ? judged.record : null;
}

/** Remove the record only when it is this user's copy of `documentKey`. */
function removeRecordOf(userId: string | null, documentKey: string | null): void {
  if (userId === null || documentKey === null) return;
  if (readRecord(userId)?.documentKey === documentKey) removeRecord();
}

/** The store's unsaved draft, if `tab` describes it. */
function tabDraft(): PlanDraft | null {
  const draft = readPlanDraft();
  return tab !== null && draft !== null && draft.id === null ? draft : null;
}

/**
 * Write the tab's plan now. Reads the store at the moment of writing, so a save that settled inside
 * the debounce owes nothing; and asks who is signed in NOW, so a plan started by somebody else is
 * never stamped as this user's.
 */
function writeNow(): void {
  cancelPendingWrite();
  const draft = tabDraft();
  if (draft === null || tab === null || tab.settled) return;
  const userId = signedInUser();
  if (userId === null || userId !== tab.ownerId) return;
  const record: UnsavedPlanRecord = {
    savedAt: Date.now(),
    userId,
    draft,
    // The client the plan is being built for, from the store — so a copy started from a client's
    // „Przypisz plan" reopens after a reload still built for that client.
    recipient: readPlanRecipient(),
    documentKey: tab.documentKey,
    saveStartedAt: tab.saveStartedAt,
  };
  try {
    const store = preferenceStore();
    if (store === null) {
      setHeld(false);
      return;
    }
    // Asked on EVERY write, from the bytes as they stand now: another tab, another trainer or
    // another build may have written since this tab last did. See `mayWriteUnsavedPlanRecord`.
    if (!mayWriteUnsavedPlanRecord(store.getItem(RECORD_KEY), userId, tab.documentKey, Date.now())) {
      setHeld(false);
      return;
    }
    store.setItem(RECORD_KEY, serialiseUnsavedPlanRecord(record));
    setHeld(true);
  } catch {
    // Quota, or storage refused outright. The plan is still in the tab; only its recovery copy is
    // missing, the leave guard says so, and the next edit tries again.
    setHeld(false);
  }
}

function flushPendingWrite(): void {
  if (pendingWrite === null) return;
  cancelPendingWrite();
  if (tab?.saveInFlight === true) return;
  writeNow();
}

/**
 * Start mirroring the unsaved plan to the device. Returns the teardown — for tests; the app never
 * stops it.
 *
 * `currentUserId` is asked at each write rather than captured once, because the session changes
 * under a running page.
 */
export function startUnsavedPlanPersistence(currentUserId: () => string | null): () => void {
  signedInUser = currentUserId;
  const unsubscribe = subscribeToPlanDraftChanges(() => {
    const draft = readPlanDraft();
    if (draft === null || draft.id !== null) {
      cancelPendingWrite();
      forgetTab();
      return;
    }
    // An unsaved tree nobody seeded through here: adopt it for whoever is signed in, or leave it
    // unwritten when nobody is.
    if (tab === null) {
      replaceTab({
        documentKey: newSlotToken(),
        ownerId: signedInUser(),
        held: false,
        saveInFlight: false,
        saveStartedAt: null,
        settled: false,
      });
    }
    if (tab?.saveInFlight === true || tab?.settled === true) return;
    cancelPendingWrite();
    pendingWrite = setTimeout(flushPendingWrite, WRITE_DEBOUNCE_MS);
  });
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flushPendingWrite();
  };
  // Another TAB wrote the key — the browser tells every other tab of the origin. Whether the device
  // still holds this tab's plan is re-read off the new bytes, so a leave guard never promises a copy
  // another tab has just replaced. It goes back to true only when this tab writes again.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== RECORD_KEY && event.key !== null) return;
    if (tab === null) return;
    const ownerId = tab.ownerId;
    setHeld(
      ownerId !== null && recordHoldsDocument(readRaw(), ownerId, tab.documentKey, Date.now()),
    );
  };
  window.addEventListener('pagehide', flushPendingWrite);
  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    unsubscribe();
    cancelPendingWrite();
    forgetTab();
    window.removeEventListener('pagehide', flushPendingWrite);
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    signedInUser = () => null;
  };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The ways in.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Put a brand-new plan in front of the builder — the create modal's seed. A new document: its key
 * is minted here, its owner is the user who created it, and its first copy is written at once, so
 * a reload in the next breath already finds it.
 *
 * If the one key is still taken by another live plan of this trainer's, the seed does NOT take it.
 * „+ Nowy program" offers that plan first, so reaching here means the trainer started from scratch
 * (which removed it) or there was none; a slot still taken is a race with another tab, and losing
 * the other tab's plan is the one thing this module exists to prevent. The new plan then lives in
 * this tab only, `held` stays false, and the leave guard says so in its destructive wording.
 */
export function seedNewPlan(
  draft: PlanDraft,
  recipient: PlanRecipient | null,
  ownerId: string | null,
): void {
  cancelPendingWrite();
  replaceTab({
    documentKey: newSlotToken(),
    ownerId,
    held: false,
    saveInFlight: false,
    settled: false,
    saveStartedAt: null,
  });
  // The store's own seed, which binds the recipient; this function adds the device's half. Every
  // new plan enters through here and nowhere else, so the two can never disagree about a seed.
  seedPlanDraft(draft, recipient);
  writeNow();
}

/**
 * What „+ Nowy program" opens with, and whether `/plans/nowy` has anything to restore. Read
 * synchronously, so a caller decides its branch before its first paint. PURE of storage writes: a
 * broken record is removed by `sweepUnsavedPlanRecord`, from an effect, never during a render.
 */
export function readNewPlanEntry(userId: string): NewPlanEntry {
  const draft = tabDraft();
  // An ASSIGNED plan stays in the store under the confirmation, but it is not leftover work.
  const own =
    draft !== null && tab !== null && !tab.settled && tab.ownerId === userId
      ? {
          draft,
          documentKey: tab.documentKey,
          held: tab.held,
          saveStartedAt: tab.saveStartedAt,
          recipient: readPlanRecipient(),
        }
      : null;
  return newPlanEntry(own, readRecord(userId), Date.now());
}

/**
 * Remove a record this user's build judged unusable. A broken record left in place would be parsed
 * and refused on every visit for a week. Another user's record, and a newer build's, are left.
 */
export function sweepUnsavedPlanRecord(userId: string): void {
  if (judgeUnsavedPlanRecord(readRaw(), userId, Date.now()).verdict === 'discard') removeRecord();
}

/**
 * Put the leftover plan in front of the builder. A plan the store already holds is left alone — the
 * store is fresher than its mirror — and one from the record is loaded as a SEED: `id` null, the
 * restored tree as the baseline, so Zapisz stays live and the leave guard still asks.
 *
 * Not a clone: the slot tokens and the document key are kept as they were written, because this is
 * the same unsaved document, not a copy of it. A save marker is carried over too: continuing does
 * not make the earlier save any less likely to have landed, so every later copy keeps saying so
 * until a save of this plan is answered.
 */
export function resumeLeftoverPlan(plan: LeftoverPlan, ownerId: string): void {
  if (plan.inStore) return;
  cancelPendingWrite();
  replaceTab({
    documentKey: plan.documentKey ?? newSlotToken(),
    ownerId,
    held: true,
    saveInFlight: false,
    settled: false,
    saveStartedAt: plan.saveStartedAt,
  });
  // Re-seeded, not loaded: a plan that was being built for a client reopens built for that client.
  seedPlanDraft(plan.draft, plan.recipient);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The ways out.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * „Zacznij od nowa" and „Usuń program": the OFFERED plan goes — from this tab if the tab holds it,
 * and from the device if the device copy is that plan. Another tab's plan in the one device key is
 * not this answer's to delete.
 */
export function discardLeftoverPlan(plan: LeftoverPlan, userId: string): void {
  if (plan.inStore || tab?.documentKey === plan.documentKey) {
    cancelPendingWrite();
    discardUnsavedPlanDraft();
  }
  removeRecordOf(userId, plan.documentKey);
}

/**
 * A deliberate sign-out: the leaving trainer's unsaved plan goes from this tab and from the device.
 * The record is removed unless it is another user's or a newer build's — the same rule the reader
 * keeps — so signing out on a shared computer never deletes a colleague's unsaved work.
 */
export function discardUnsavedPlansOf(userId: string | null): void {
  cancelPendingWrite();
  discardUnsavedPlanDraft();
  if (userId === null) return;
  const verdict = judgeUnsavedPlanRecord(readRaw(), userId, Date.now()).verdict;
  if (verdict === 'usable' || verdict === 'discard') removeRecord();
}

/**
 * Any session END — an expiry, a refresh that failed, another account signing in — takes the
 * unsaved plan out of this TAB, and leaves the device copy: the same trainer signing back in gets it
 * back, and the reader already hides it from anybody else.
 */
export function dropUnsavedPlanFromTab(): void {
  cancelPendingWrite();
  discardUnsavedPlanDraft();
}

/**
 * The first successful save of a new plan: the server's tree replaces the seed, and the recovery
 * copy has nothing left to recover. One act, so no caller can do the first half and forget the
 * second — a record outliving its save would offer the trainer a plan that already exists, and
 * „Kontynuuj" would POST it a second time.
 *
 * Settled against the `attempt` captured when the POST left, never against the tab as it is now:
 * a session that ended while the POST was on the wire has already emptied the tab, and the record
 * still has to go. If the signed-in user is no longer the one who sent it, the tree is NOT loaded —
 * it is the previous trainer's plan, and this tab now belongs to somebody else.
 *
 * `stored: null` is the save that succeeded with a response this app cannot represent. The seed goes
 * all the same: left in the store, a pending write or a pagehide would put it back as unsaved.
 *
 * Answers `false` when the session changed under the save, so the caller does not carry the new
 * trainer to the previous one's plan.
 */
export function settleSavedNewPlan(stored: PlanDraft | null, attempt: FirstSaveAttempt | null): boolean {
  const documentKey = attempt?.documentKey ?? tab?.documentKey ?? null;
  const ownerId = attempt?.ownerId ?? tab?.ownerId ?? null;
  removeRecordOf(ownerId, documentKey);
  if (ownerId !== signedInUser()) return false;
  cancelPendingWrite();
  if (stored === null) discardUnsavedPlanDraft();
  else loadPlanDraft(stored);
  return true;
}

/**
 * The unsaved plan was ASSIGNED: it became a client's plan and has nothing left to recover.
 *
 * Exported for the one settle that does not go through this file yet. When the copy-and-assign flow
 * lands (`feat/plan-copy-assign-flow`), its `settleAssignedPlanDraft()` calls this. That flow leaves
 * the assigned tree in the store, still with no id, under the confirmation — so removing the record
 * is not enough: the next store change or a pagehide would write it straight back, and „+ Nowy
 * program" would offer a plan the client already has. The tab is therefore marked settled, which
 * stops every write for it until another plan is seeded or the store moves on. The record goes only
 * when it is this tab's plan.
 */
export function settleAssignedNewPlan(): void {
  cancelPendingWrite();
  if (tab === null) return;
  removeRecordOf(tab.ownerId, tab.documentKey);
  tab.settled = true;
  setHeld(false);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The first save's round trip. No-ops for a stored plan, which has no `tab`.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * What a first save knew about its plan when the POST left: which document, whose, and the marker
 * the copy carried before it. Every answer is settled against THIS, because the tab may have moved
 * on — or been emptied by a session end — by the time the answer arrives.
 */
export interface FirstSaveAttempt {
  readonly documentKey: string;
  readonly ownerId: string | null;
  readonly priorMarker: number | null;
}

/**
 * The POST is about to leave: write the marker, then hold every write until it is answered. `null`
 * for a stored plan, which has no device copy to mark.
 */
export function markFirstSaveStarted(): FirstSaveAttempt | null {
  if (tab === null || tabDraft() === null) return null;
  const attempt = {
    documentKey: tab.documentKey,
    ownerId: tab.ownerId,
    priorMarker: tab.saveStartedAt,
  };
  tab.saveStartedAt = Date.now();
  writeNow();
  tab.saveInFlight = true;
  return attempt;
}

/**
 * How a first save that did NOT settle ended, and what that means for the marker.
 *
 * · `refused` — the server answered that nothing was stored. This attempt's marker goes; a marker
 *   the plan carried from an EARLIER unanswered save stays, because this refusal says nothing about
 *   that one.
 * · `unanswered` — no verdict: a transport failure, or a success this app could not place. The
 *   server may hold the plan, so the new marker stays.
 *
 * Either way the writes resume and carry whatever was edited while the answer was on its way. Safe
 * to call more than once and after `settleSavedNewPlan`: it only acts on the attempt's own document
 * while a save of it is still in flight, which is what lets the save hook call it on EVERY settle
 * as the one exit no path can skip.
 */
export function endFirstSave(attempt: FirstSaveAttempt | null, outcome: 'refused' | 'unanswered'): void {
  if (attempt === null || tab === null || tab.documentKey !== attempt.documentKey) return;
  if (!tab.saveInFlight) return;
  tab.saveInFlight = false;
  if (outcome === 'refused') tab.saveStartedAt = attempt.priorMarker;
  writeNow();
}

/**
 * Whether the device holds a copy of the plan this tab is building — what the leave guard may
 * promise. `false` for a stored plan, and for an unsaved one whose last write was refused.
 */
export function useDeviceCopyHeld(): boolean {
  return useSyncExternalStore(
    (listener) => {
      heldListeners.add(listener);
      return () => {
        heldListeners.delete(listener);
      };
    },
    () => tab?.held === true,
  );
}

/**
 * The identity of the unsaved plan this tab is building, the same one its device copy keeps across a
 * reload and a resume; `null` for a stored plan and once the plan is settled. The builder's assistant
 * keys its conversation by it (ai-plan-builder R13), so a plan that comes back after a reload comes
 * back to its conversation.
 */
export function useUnsavedPlanDocumentKey(): string | null {
  return useSyncExternalStore(
    (listener) => {
      heldListeners.add(listener);
      return () => {
        heldListeners.delete(listener);
      };
    },
    () => (tab === null || tab.settled ? null : tab.documentKey),
  );
}
